import {
  RadioGroup,
  Radio,
  Text,
  Divider,
  spaces,
  Tooltip,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

import { FilterDropdown } from '@common';

export enum SuggestionSourceType {
  AllCompanyProducts = 'allCompanyProducts',
  AllEmployeeGroupsProducts = 'allEmployeeGroupsProducts',
}

export type SuggestionSourceOption = {
  label: string;
  value: SuggestionSourceType | string;
  productsCount: number;
};

export const ALL_COMPANY_PRODUCTS_OPTION: SuggestionSourceOption = {
  label: '', // Not displayed - component uses translation key instead
  value: SuggestionSourceType.AllCompanyProducts,
  productsCount: 0,
};

export const ALL_EMPLOYEE_GROUPS_PRODUCTS_OPTION: SuggestionSourceOption = {
  label: '', // Not displayed - component uses translation key instead
  value: SuggestionSourceType.AllEmployeeGroupsProducts,
  productsCount: 0,
};

type SuggestionSourceSelectorProps = {
  availableOptions: SuggestionSourceOption[];
  selectedOption: SuggestionSourceOption;
  onSelect: (newSelectedOption: SuggestionSourceOption) => void;
};

export const SuggestionSourceSelector = ({
  availableOptions,
  selectedOption,
  onSelect,
}: SuggestionSourceSelectorProps) => {
  const { t } = useTranslation('common');

  // the dynamic options (for each employee group)
  const employeeGroupsOptions = availableOptions.filter(
    option =>
      option.value !== ALL_COMPANY_PRODUCTS_OPTION.value &&
      option.value !== ALL_EMPLOYEE_GROUPS_PRODUCTS_OPTION.value
  );

  return (
    <FilterDropdown
      id="buy-it-again-suggestion-source-selector"
      someOptionIsChecked={false}
      filterName={
        selectedOption.value === SuggestionSourceType.AllCompanyProducts
          ? t('storefront.buyItAgain.sourceSelector.allCompanyPurchases')
          : t('storefront.buyItAgain.sourceSelector.filterNameGroups', {
              label: selectedOption.label,
            })
      }
      dropdownContentProps={{
        minWidth: 'fit-content',
        padding: spaces.space_m,
      }}
      dropdownContent={
        <RadioGroup
          width="100%"
          stack
          aria-label="buy-it-again-suggestions-options"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const option = availableOptions.find(
              option => option.value === e.target.value
            );
            if (option) {
              onSelect({
                label: option.label,
                value: option.value,
                productsCount: option.productsCount,
              });
            }
          }}>
          {/* 1st fixed option (all company products) */}
          <Radio
            id={ALL_COMPANY_PRODUCTS_OPTION.value}
            value={ALL_COMPANY_PRODUCTS_OPTION.value}
            checked={selectedOption.value === ALL_COMPANY_PRODUCTS_OPTION.value}
            disabled={
              availableOptions.find(
                option => option.value === ALL_COMPANY_PRODUCTS_OPTION.value
              )?.productsCount === 0
            }>
            {t('storefront.buyItAgain.sourceSelector.allCompanyPurchases')}
          </Radio>

          <Divider />
          <Text intent="secondary">
            {t('storefront.buyItAgain.sourceSelector.assignedGroupsLabel')}
          </Text>

          {employeeGroupsOptions.length > 0 ? (
            <>
              {/* 2nd fixed option ("all" employee groups products) */}
              {employeeGroupsOptions.length > 1 && (
                <Radio
                  id={ALL_EMPLOYEE_GROUPS_PRODUCTS_OPTION.value}
                  value={ALL_EMPLOYEE_GROUPS_PRODUCTS_OPTION.value}
                  checked={
                    selectedOption.value ===
                    ALL_EMPLOYEE_GROUPS_PRODUCTS_OPTION.value
                  }
                  disabled={
                    availableOptions.find(
                      option =>
                        option.value ===
                        ALL_EMPLOYEE_GROUPS_PRODUCTS_OPTION.value
                    )?.productsCount === 0
                  }>
                  {t('storefront.buyItAgain.sourceSelector.allEmployeeGroups')}
                </Radio>
              )}

              {/* Dynamic options (employee groups) */}
              {employeeGroupsOptions.map(option => {
                return (
                  <Radio
                    key={option.value}
                    id={`employee-group-${option.value}`}
                    value={option.value}
                    checked={selectedOption.value === option.value}
                    disabled={option.productsCount === 0}>
                    <Tooltip
                      content={t(
                        'storefront.buyItAgain.sourceSelector.noPurchaseHistory'
                      )}
                      disabled={option.productsCount > 0}
                      placement="bottom"
                      wrapperWidth="100%">
                      <span>{option.label}</span>
                    </Tooltip>
                  </Radio>
                );
              })}
            </>
          ) : (
            <Text variant="body-2" intent="disabled">
              {t('storefront.buyItAgain.sourceSelector.noGroupsAssigned')}
            </Text>
          )}
        </RadioGroup>
      }
    />
  );
};
